import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  Signal,
  signal,
  inject,
} from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Environment, ENVIRONMENT } from '@web/config';
import { InPostPointSelect } from '../../types/in-post-point-select';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { PrimengDialogDirective } from '../../../dialog/directives/primeng-dialog.directive';
import { PrimengDialogCustomCloseEventDirective } from '../../../dialog/directives/primeng-dialog-custom-close-event.directive';
import { InPostPointSelectWidgetComponent } from '../in-post-point-select-widget/in-post-point-select-widget.component';
import { isNil, isNotNil, Nillable } from '@web/utils';
import { InPostPointLocation } from '../../types/in-post-point-location';

@Component({
  selector: 'app-in-post-point-select',
  imports: [
    Dialog,
    Button,
    PrimengDialogDirective,
    PrimengDialogCustomCloseEventDirective,
    InPostPointSelectWidgetComponent,
  ],
  templateUrl: './in-post-point-select.component.html',
  styleUrl: './in-post-point-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InPostPointSelectComponent),
      multi: true,
    },
  ],
})
export class InPostPointSelectComponent implements ControlValueAccessor {
  private readonly environment = inject<Environment>(ENVIRONMENT);

  public readonly country = input.required<string>();
  public readonly international = input<boolean>(false);

  protected readonly dialogVisibility = signal(false);
  protected readonly selected = signal<Nillable<InPostPointSelect>>(null);
  protected readonly location = this.locationSignal();
  /* prettier-ignore */
  protected readonly choosePointButtonLabel = this.choosePointButtonLabelSignal();

  private onChange!: (value: InPostPointSelect) => void;
  private onTouched!: () => void;

  private isTouched = false;

  public readonly customCloseEvent = () => {
    this.hideDialog();
  };

  public writeValue(value: InPostPointSelect): void {
    this.selected.set(value);
  }

  public registerOnChange(fn: (value: InPostPointSelect) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public handlePointSelectEvent(point: InPostPointSelect): void {
    this.selected.set(point);
    this.markAsTouched();
    this.onChange(point);
    this.hideDialog();
  }

  public handleChoosePointClickEvent(): void {
    this.showDialog();
  }

  protected siteKey(): string {
    return this.environment.InPost.SiteKey;
  }

  protected isSandbox(): boolean {
    return this.environment.InPost.Sandbox;
  }

  protected choosePointButtonLabelSignal(): Signal<string> {
    return computed(() => {
      if (isNil(this.selected())) {
        return 'Wybierz punkt odbioru';
      }

      return 'Zmień punkt odbioru';
    });
  }

  private markAsTouched(): void {
    if (this.isTouched) {
      return;
    }

    this.isTouched = true;

    this.onTouched();
  }

  private showDialog(): void {
    this.dialogVisibility.set(true);
  }

  private hideDialog(): void {
    this.dialogVisibility.set(false);
  }

  private locationSignal(): Signal<Nillable<InPostPointLocation>> {
    return computed(() => {
      const selected = this.selected();

      if (
        isNotNil(selected?.location?.longitude) &&
        isNotNil(selected?.location?.latitude)
      ) {
        return new InPostPointLocation({
          longitude: Number(selected?.location?.longitude),
          latitude: Number(selected?.location?.latitude),
        });
      }

      return null;
    });
  }
}
